import {
  Box,
  Modal,
  Fade,
  Grid,
  Typography,
  OutlinedInput,
  InputLabel,
  FormHelperText,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import CustomButton from "../../shared/CustomButton";

import { sendMail } from "../state/actions";
import { useDispatch } from "react-redux";
import { useState } from "react";

const style = {
  position: "absolute",
  top: "10%",
  minHeight: "calc(100vh - 20%)",
  width: "calc(100vw - 50%)",
  marginLeft: "25%",
  bgcolor: "#F8FAFB",
  border: "none",
  borderRadius: "6px",
};

/**
 * @param {Object} handleClose close modal handler
 * @param {String} referenceNumber message's reference number
 * @returns
 */
const MailModal = ({
  handleClose,
  referenceNumbers,
  success,
  error,
  sendingMail,
}) => {
  const dispatch = useDispatch();
  const [processing, setProcessing] = useState(false);
  //Form validation
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email("Not a valid email address")
      .required("This field is mandatory"),
    subject: yup
      .string()
      .required("This field is mandatory")
      .min(4, "Minimum 4 characters")
      .max(30, "Maximum 30 characters"),
    body: yup.string().required("This field is mandatory"),
  });
  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      email: "",
      subject: "",
      body: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setProcessing(true);
      dispatch(
        sendMail({ ...values, references: referenceNumbers }, success, error)
      )
        .then(() => {
          handleClose();
        })
        .finally(() => {
          setProcessing(false);
        });
    },
  });
  return (
    <Modal
      aria-labelledby="mail-message-modal"
      aria-describedby="mail-message-modal"
      open={true}
      onClose={handleClose}
      disableEnforceFocus={true}
      disableAutoFocus={true}
      sx={{ zIndex: 10000 }}
    >
      <Fade in={true}>
        <Box sx={style}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{
              height: "64px",
              p: "15px",
              backgroundColor: "#050D2E",
              borderRadius: "6px 6px 0px 0px",
            }}
          >
            <Grid item>
              <Typography
                sx={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: 550 }}
              >
                Email Message
              </Typography>
            </Grid>
            <Grid item>
              <CloseIcon
                sx={{ color: "#ffffff", cursor: "pointer" }}
                onClick={handleClose}
              />
            </Grid>
          </Grid>
          <Box>
            <Grid
              container
              direction="column"
              sx={{
                p: "20px",
              }}
            >
              <Grid item>
                <InputLabel
                  htmlFor="messages"
                  sx={{
                    color: "#050D2E",
                    fontSize: ".875rem",
                    fontWeight: 550,
                    letterSpacing: "-0.5px",
                    lineHeight: "16px",
                    pb: 1,
                    pt: 2,
                  }}
                >
                  Recipient
                </InputLabel>
                <OutlinedInput
                  name="email"
                  id="email"
                  sx={{
                    height: "36px",
                    "& .MuiInputBase-root": { height: "36px" },
                    "& .MuiOutlinedInput": { height: "36px" },
                    backgroundColor: "#ffffff",
                  }}
                  fullWidth
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.email)}
                />
                {Boolean(formik.errors.email) && (
                  <FormHelperText id="email" error={true}>
                    {formik.errors.email}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item>
                <InputLabel
                  htmlFor="messages"
                  sx={{
                    color: "#050D2E",
                    fontSize: ".875rem",
                    fontWeight: 550,
                    letterSpacing: "-0.5px",
                    lineHeight: "16px",
                    pb: 1,
                    pt: 2,
                  }}
                >
                  Subject
                </InputLabel>
                <OutlinedInput
                  name="subject"
                  id="subject"
                  sx={{
                    height: "36px",
                    "& .MuiInputBase-root": { height: "36px" },
                    "& .MuiOutlinedInput": { height: "36px" },
                    backgroundColor: "#ffffff",
                  }}
                  fullWidth
                  value={formik.values.subject}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.subject)}
                />
                {Boolean(formik.errors.subject) && (
                  <FormHelperText id="subject" error={true}>
                    {formik.errors.subject}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item>
                <TextField
                  id="body"
                  name="body"
                  multiline
                  maxRows={7}
                  minRows={7}
                  fullWidth
                  value={formik.values.body}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.body)}
                  sx={{
                    backgroundColor: "#ffffff",
                    my: 2,
                  }}
                />
                {Boolean(formik.errors.body) && (
                  <FormHelperText id="body" error={true}>
                    {formik.errors.body}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item>
                <CustomButton
                  type="update"
                  processing={processing}
                  disabled={processing}
                  text="Send"
                  icon={<SendIcon />}
                  action={formik.handleSubmit}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default MailModal;
